import { ErrorState, Spinner, EmptyState } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { fmtValue, fmtPnl, fmtTime } from '../../broker/bookUtils';
import { RowStatusBadge } from './ManualBadges';

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
];
const PAGE_SIZES = [25, 50, 100];

// Backend exit_reason → readable label. Supports the new canonical values and legacy aliases.
// Only what the backend returns is displayed; the reason is never inferred from prices/status/target/SL.
const EXIT_REASON_LABELS = {
  // canonical / new
  MANUAL: 'Manual',
  TARGET: 'Target',
  SL: 'SL',
  AUTO_SQ_OFF: 'Auto Square-Off',
  // legacy aliases
  MANUAL_EXIT: 'Manual',
  STOP_LOSS: 'SL',
  MANUAL_INTRADAY_AUTO_SQUARE_OFF: 'Auto Square-Off',
  OTHER: 'Other',
};
const exitReasonLabel = (v) => (v == null || v === '' ? '—' : (EXIT_REASON_LABELS[v] || fmtValue(v)));

// P&L cell — bull/bear/muted; backend value only (bookUtils.fmtPnl renders null/'' → "—").
function PnlCell({ value }) {
  const num = Number(value);
  const cls = value == null || value === '' ? 'text-txt-secondary' : num > 0 ? 'text-bull' : num < 0 ? 'text-bear' : 'text-txt-secondary';
  return <span className={cn('font-medium', cls)}>{fmtPnl(value)}</span>;
}

function TradeIdCell(v) {
  if (!v || typeof v !== 'string') return fmtValue(v);
  const short = v.length > 16 ? v.slice(0, 16) + '…' : v;
  return <span title={v} className="whitespace-nowrap">{short}</span>;
}

// Desktop-only: shows just the time portion of Entry/Exit Time (the Trade Book row already sits
// under a single selected date filter, so repeating the date in every row wastes vertical space).
// Safety: this does NOT re-parse the timestamp or reinterpret its timezone. bookUtils.fmtTime(value)
// remains the sole source of truth — it already returns time-only for epoch-numeric inputs, and
// "DD/MM/YYYY, HH:MM:SS" (en-IN toLocaleString, comma-separated) for the ISO-string inputs Trade
// Book actually returns. This only string-slices fmtTime's own already-correct output after the
// last comma to drop the leading date; the full fmtTime() string is preserved as the tooltip, so
// both the visible time and the tooltip always agree with the exact same interpretation.
function TimeOnlyCell({ value }) {
  const full = fmtTime(value);
  if (full === '—') return <span>—</span>;
  const commaIdx = full.lastIndexOf(',');
  const short = commaIdx >= 0 ? full.slice(commaIdx + 1).trim() : full;
  return <span title={full} className="whitespace-nowrap">{short}</span>;
}

// Side may be BUY/SELL or LONG/SHORT depending on the backend's trade-cycle convention —
// color by either without assuming a single literal value.
function SideBadge({ side }) {
  const s = String(side || '').toUpperCase();
  const isLong = s === 'BUY' || s === 'LONG';
  const isShort = s === 'SELL' || s === 'SHORT';
  const cls = isLong ? 'bg-bull/10 border-bull/30 text-bull' : isShort ? 'bg-bear/10 border-bear/30 text-bear' : 'bg-bg-hover border-bg-border text-txt-muted';
  return <span className={cn('badge border', cls)}>{fmtValue(side)}</span>;
}

function baseContext(row) {
  const primary = String(row?.symbol || '').trim().toUpperCase();
  const base = String(row?.base_symbol || row?.source_symbol || '').trim().toUpperCase();
  return base && base !== primary ? base : '';
}

function SymbolCell({ row }) {
  const base = baseContext(row);
  return (
    <div className="min-w-0">
      <div className="text-txt-primary font-medium truncate">{fmtValue(row?.symbol)}</div>
      {base && <div className="mt-0.5 text-2xs text-txt-muted truncate">Base: {fmtValue(base)}</div>}
    </div>
  );
}

// Mobile card presentation of one trade-cycle row — same data/formatters as the desktop
// table's columns, just laid out for touch instead of a scrollable table row.
function TradeCard({ row }) {
  return (
    <div className="rounded-lg border border-bg-border bg-bg-surface/40 p-2 mb-1.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="min-w-0">
          <span className="font-medium text-txt-primary truncate block">{fmtValue(row.symbol)}</span>
          {baseContext(row) && <span className="text-2xs font-mono text-txt-muted truncate block">Base: {fmtValue(baseContext(row))}</span>}
        </div>
        <SideBadge side={row.side} />
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-1">
        <span className="text-txt-secondary">{fmtValue(row.status)}</span>
        {row.exit_reason != null && row.exit_reason !== '' && <span> · {exitReasonLabel(row.exit_reason)}</span>}
      </div>

      <div className="text-2xs font-mono text-txt-secondary mb-0.5">
        Entry {fmtValue(row.entry_quantity)} @ {fmtValue(row.entry_average_price)} · Exit {fmtValue(row.exit_quantity)} @ {fmtValue(row.exit_average_price)}
      </div>

      <div className="text-2xs font-mono mb-1">
        <span className="text-txt-label">P&L </span>
        <PnlCell value={row.realized_profit} />
      </div>

      <div className="text-2xs font-mono text-txt-muted">
        {fmtTime(row.entry_time)} → {fmtTime(row.exit_time)} · {TradeIdCell(row.trade_id)}
      </div>
    </div>
  );
}

// Columns mirror OrdersTable/PositionsTable conventions. All values come straight from the backend
// trade-cycle row — no frontend reconstruction, no LTP-store substitution, no P&L computation.
// Desktop-only presentation: `label`/`title` abbreviate long headers (native tooltip spells the
// abbreviation out) and `align` right-aligns numeric columns — mobile's TradeCard renders its own
// markup below and never reads these column defs, so none of this affects mobile.
const COLUMNS = [
  { key: 'symbol', label: 'Symbol', render: (_v, row) => <SymbolCell row={row} /> },
  { key: 'side', label: 'Side', render: (v) => <SideBadge side={v} /> },
  { key: 'product', label: 'Prod', title: 'Product', render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'status', label: 'Status', render: (v) => <RowStatusBadge status={fmtValue(v)} /> },
  { key: 'entry_quantity', label: 'Entry Qty', align: 'right', render: (v) => fmtValue(v) },
  { key: 'entry_average_price', label: 'Entry Avg', align: 'right', render: (v) => fmtValue(v) },
  { key: 'exit_quantity', label: 'Exit Qty', align: 'right', render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'exit_average_price', label: 'Exit Avg', align: 'right', render: (v) => fmtValue(v), mobileHidden: true },
  { key: 'open_quantity', label: 'Open Qty', align: 'right', render: (v) => fmtValue(v) },
  { key: 'realized_profit', label: 'Rld P&L', title: 'Realized P&L', align: 'right', render: (v) => <PnlCell value={v} /> },
  { key: 'entry_time', label: 'Entry Time', render: (v) => <TimeOnlyCell value={v} />, mobileHidden: true },
  { key: 'exit_time', label: 'Exit Time', render: (v) => <TimeOnlyCell value={v} />, mobileHidden: true },
  { key: 'exit_reason', label: 'Exited By', title: 'Exit reason/source', render: (v) => exitReasonLabel(v), mobileHidden: true },
  { key: 'trade_id', label: 'Trade ID', render: TradeIdCell, mobileHidden: true },
];

function FilterSelect({ value, onChange, children, title, className }) {
  return (
    <select
      value={value}
      title={title}
      onChange={(e) => onChange(e.target.value)}
      className={cn('bg-bg-surface border border-bg-border rounded px-3 py-1.5 min-h-[40px] text-sm font-mono text-txt-primary focus:outline-none focus:border-amber/60 lg:px-1.5 lg:py-0.5 lg:min-h-0 lg:text-xs', className)}
    >
      {children}
    </select>
  );
}

// Controlled: the card owns the filter/page state (so the query key memoizes there). This renders the
// filters, the backend rows, the excluded-fills warning, and pagination.
export default function TradeBookTable({
  data,
  loading = false,
  error = null,
  onRetry,
  symbols = [],
  filters,        // { date, status, symbol, pageSize }
  page,
  maxDate,
  onDateChange,
  onStatusChange,
  onSymbolChange,
  onPageChange,
  onPageSizeChange,
}) {
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.page_size ?? filters.pageSize;
  const excluded = data?.excluded_unattributed_fills ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / (pageSize || 1)));
  const filtersActive = filters.status !== 'ALL' || !!filters.symbol || filters.date !== maxDate;

  const filterRow = (
    <div className="grid grid-cols-2 gap-1.5 mb-2 sm:flex sm:flex-row sm:items-center sm:flex-wrap">
      <input
        type="date"
        value={filters.date}
        max={maxDate}
        onChange={(e) => onDateChange(e.target.value)}
        className="w-full sm:w-auto bg-bg-surface border border-bg-border rounded px-3 py-1.5 min-h-[40px] text-sm font-mono text-txt-primary focus:outline-none focus:border-amber/60 lg:px-1.5 lg:py-0.5 lg:min-h-0 lg:text-xs"
      />
      <FilterSelect value={filters.status} onChange={onStatusChange} title="Status" className="w-full sm:w-auto">
        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </FilterSelect>
      <FilterSelect value={filters.symbol} onChange={onSymbolChange} title="Symbol" className="col-span-2 w-full sm:w-auto">
        <option value="">All Symbols</option>
        {symbols.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </FilterSelect>
    </div>
  );

  const pagination = (
    <div className="flex items-center justify-between gap-3 mt-2 text-2xs font-mono text-txt-secondary">
      <div className="flex items-center gap-2">
        <span className="text-txt-label">Rows</span>
        <FilterSelect value={String(filters.pageSize)} onChange={(v) => onPageSizeChange(Number(v))} title="Page size">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </FilterSelect>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="mt-page px-2 py-0.5 rounded border border-bg-border hover:bg-bg-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span>Page {page} of {pageCount}</span>
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          className="mt-page px-2 py-0.5 rounded border border-bg-border hover:bg-bg-hover disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  if (error) {
    return (
      <div>
        {filterRow}
        <ErrorState message={error} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div>
      {filterRow}
      {excluded > 0 && (
        <div className="mb-3 rounded-md border border-warn/30 bg-warn/10 px-3 py-2 text-2xs font-mono text-warn">
          Some legacy fills could not be attributed to a Manual watchlist row and are not shown.
          {` (${excluded} legacy fill${excluded === 1 ? '' : 's'} excluded)`}
        </div>
      )}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-8 text-txt-muted">
          <Spinner size={14} /> <span className="text-2xs font-mono">Loading trade book…</span>
        </div>
      ) : (
        <>
          {/* Desktop — compact local table (not the shared ui/DataTable), matching the Positions
              table's header/row padding so switching Positions → Orders → Trade Book has no
              visible density jump. No other DataTable-based screen is affected. Bounded to ~6
              data rows of the current page (a single overflow-auto container handles vertical +
              horizontal scroll together, so a 7th+ row scrolls internally without any
              page-level horizontal overflow; existing pagination/page-size semantics are
              unchanged — this only bounds the visible height of whatever page is loaded). The
              sticky, shaded header (bg-bg-hover — visibly distinct from the plain row
              background, opaque so scrolling row text never shows through) stays pinned during
              that scroll. */}
          <div className="hidden lg:block">
            {!items.length ? (
              <EmptyState title={filtersActive ? 'No trades found for the selected filters' : 'No trades found'} />
            ) : (
              <div className="overflow-auto max-h-[14.5rem]">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          title={col.title}
                          className={cn(
                            'sticky top-0 z-10 bg-bg-hover border-b border-bg-border',
                            'py-1.5 px-2.5 text-txt-label uppercase tracking-widest text-2xs font-semibold whitespace-nowrap',
                            col.align === 'right' ? 'text-right' : 'text-left',
                            col.mobileHidden && 'hidden sm:table-cell',
                          )}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, i) => (
                      <tr key={row.trade_id ?? i} className="border-b border-bg-border/50">
                        {COLUMNS.map((col) => (
                          <td
                            key={col.key}
                            className={cn(
                              'py-2 px-2.5 text-txt-secondary font-mono',
                              col.align === 'right' ? 'text-right' : 'text-left',
                              col.mobileHidden && 'hidden sm:table-cell',
                            )}
                          >
                            {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="lg:hidden">
            {items.length === 0 ? (
              <EmptyState title={filtersActive ? 'No trades found for the selected filters' : 'No trades found'} />
            ) : (
              items.map((row, i) => <TradeCard key={row.trade_id ?? i} row={row} />)
            )}
          </div>
        </>
      )}
      {total > 0 && pagination}
    </div>
  );
}
